/**
 * Servicio de utilidades para manejar errores, notificaciones y otras funciones comunes
 */

import { getUserFriendlyErrorMessage, processApiError } from '../utils/errorHandler';

// Función para manejar errores de la API
export const handleApiError = (
  error,
  defaultMessage = 'Ha ocurrido un error. Por favor, inténtalo de nuevo.',
) => {
  console.log('handleApiError - Error recibido:', error);

  // Si es un error de tipo UNAUTHORIZED, devolver mensaje específico
  if (error.type === 'UNAUTHORIZED') {
    return 'Tu sesión ha expirado. Por favor, inicia sesión de nuevo.';
  }

  // Si es un error de tipo VALIDATION_ERROR con errores específicos
  if (error.type === 'VALIDATION_ERROR' && error.data && error.data.errors) {
    const errorMessages = [];
    for (const field in error.data.errors) {
      if (Array.isArray(error.data.errors[field])) {
        errorMessages.push(error.data.errors[field][0]);
      }
    }
    if (errorMessages.length > 0) {
      return errorMessages.join('. ');
    }
  }

  // Si es un error de respuesta HTTP con mensaje específico del servidor
  if (error.response && error.response.data) {
    const responseData = error.response.data;

    // Si el servidor devuelve un mensaje específico
    if (responseData.message) {
      return responseData.message;
    }

    // Si hay detalles específicos del error
    if (responseData.details) {
      if (Array.isArray(responseData.details)) {
        return responseData.details.join('. ');
      } else if (typeof responseData.details === 'string') {
        return responseData.details;
      }
    }
  }

  // Si es un error de red
  if (error.message === 'Network Error') {
    return 'Error de conexión. Por favor, verifica tu conexión a internet.';
  }

  // Si es un error de timeout
  if (error.code === 'ECONNABORTED') {
    return 'La solicitud ha tardado demasiado tiempo. Por favor, inténtalo de nuevo.';
  }

  // Si el error tiene un mensaje directo
  if (error.message && typeof error.message === 'string') {
    return error.message;
  }

  // Procesar el error si aún no ha sido procesado
  const processedError = error.type ? error : processApiError(error, defaultMessage);

  // Obtener un mensaje amigable para el usuario
  return getUserFriendlyErrorMessage(processedError, defaultMessage);
};

// Función para formatear fechas
export const formatDate = (date, format = 'DD/MM/YYYY') => {
  if (!date) return '';

  const d = new Date(date);

  // Si la fecha no es válida
  if (isNaN(d.getTime())) return '';

  // Formatear según el formato solicitado
  switch (format) {
    case 'DD/MM/YYYY':
      return `${padZero(d.getDate())}/${padZero(d.getMonth() + 1)}/${d.getFullYear()}`;
    case 'MM/DD/YYYY':
      return `${padZero(d.getMonth() + 1)}/${padZero(d.getDate())}/${d.getFullYear()}`;
    case 'YYYY-MM-DD':
      return `${d.getFullYear()}-${padZero(d.getMonth() + 1)}-${padZero(d.getDate())}`;
    case 'DD MMM YYYY': {
      const months = [
        'Ene',
        'Feb',
        'Mar',
        'Abr',
        'May',
        'Jun',
        'Jul',
        'Ago',
        'Sep',
        'Oct',
        'Nov',
        'Dic',
      ];
      return `${padZero(d.getDate())} ${months[d.getMonth()]} ${d.getFullYear()}`;
    }
    case 'relative':
      return getRelativeTime(d);
    default:
      return `${padZero(d.getDate())}/${padZero(d.getMonth() + 1)}/${d.getFullYear()}`;
  }
};

// Función auxiliar para añadir ceros a la izquierda
const padZero = (num) => {
  return num < 10 ? `0${num}` : num;
};

// Función para obtener tiempo relativo
const getRelativeTime = (date) => {
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffMonth = Math.floor(diffDay / 30);
  const diffYear = Math.floor(diffMonth / 12);

  if (diffSec < 60) {
    return 'hace un momento';
  } else if (diffMin < 60) {
    return `hace ${diffMin} ${diffMin === 1 ? 'minuto' : 'minutos'}`;
  } else if (diffHour < 24) {
    return `hace ${diffHour} ${diffHour === 1 ? 'hora' : 'horas'}`;
  } else if (diffDay < 30) {
    return `hace ${diffDay} ${diffDay === 1 ? 'día' : 'días'}`;
  } else if (diffMonth < 12) {
    return `hace ${diffMonth} ${diffMonth === 1 ? 'mes' : 'meses'}`;
  } else {
    return `hace ${diffYear} ${diffYear === 1 ? 'año' : 'años'}`;
  }
};

// Función para formatear moneda
export const formatCurrency = (amount, currency = 'EUR', position = 'after') => {
  if (amount === null || amount === undefined) return '';

  // Formatear el número con 2 decimales
  const formattedAmount = parseFloat(amount).toFixed(2);

  // Obtener el símbolo de la moneda
  let symbol;
  switch (currency) {
    case 'EUR':
      symbol = '€';
      break;
    case 'USD':
      symbol = '$';
      break;
    case 'GBP':
      symbol = '£';
      break;
    default:
      symbol = currency;
  }

  // Devolver el formato según la posición
  return position === 'before' ? `${symbol}${formattedAmount}` : `${formattedAmount} ${symbol}`;
};

// Función para validar email
export const isValidEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

// Función para validar contraseña
export const isValidPassword = (password, options = {}) => {
  const {
    minLength = 8,
    requireUppercase = true,
    requireLowercase = true,
    requireNumbers = true,
    requireSpecialChars = true,
  } = options;

  // Validar longitud mínima
  if (password.length < minLength) {
    return false;
  }

  // Validar mayúsculas
  if (requireUppercase && !/[A-Z]/.test(password)) {
    return false;
  }

  // Validar minúsculas
  if (requireLowercase && !/[a-z]/.test(password)) {
    return false;
  }

  // Validar números
  if (requireNumbers && !/[0-9]/.test(password)) {
    return false;
  }

  // Validar caracteres especiales
  // eslint-disable-next-line no-useless-escape
  if (requireSpecialChars && !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/.test(password)) {
    return false;
  }

  return true;
};

// Función para truncar texto
export const truncateText = (text, maxLength = 100) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Función para generar un ID único
export const generateUniqueId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

// Función para obtener la edad a partir de la fecha de nacimiento
export const getAge = (birthDate) => {
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age;
};

// Función para verificar si un usuario tiene un rol específico
export const hasRole = (user, roles) => {
  if (!user || !user.role) return false;
  if (Array.isArray(roles)) {
    return roles.includes(user.role);
  }
  return user.role === roles;
};

// Exportar todas las funciones como un objeto
const utilsService = {
  handleApiError,
  formatDate,
  formatCurrency,
  isValidEmail,
  isValidPassword,
  truncateText,
  generateUniqueId,
  getAge,
  hasRole,
};

export default utilsService;
